import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonNew, ButtonNewType } from '@components-library/ButtonNew/ButtonNew';
import formStyles from '@components-library/Forms/shared/styles/form.module.scss';
import { XIcon } from '@components-library/Icons/iconsComponents';
import ModalPortal from '@components-shared/modal/ModalPortal';
import { keyMap, KeyMapKeys } from '@locales/lokalise/keyMap';
import { ButtonProps } from 'antd';
import cn from 'classnames';
import styles from './modalWrapper.styles.module.scss';

export type ModalWrapperViewMode = 'primary' | 'secondary';

interface IModalWrapperProps {
  title: string;
  closeHandler: () => void;
  children: ReactNode;
  size?: 'large' | 'fullScreen';
  width?: 'maxWidth';
  actionsPanel?: ReactNode;
  className?: string;
  viewMode?: ModalWrapperViewMode;
  classes?: {
    body?: string;
    inner?: string;
    header?: string;
    title?: string;
  };
}

interface IModalWrapperActions {
  confirmButtonProps?: IActionButtonProps;
  closeButtonProps?: Pick<IActionButtonProps, 'size' | 'type' | 'className'>;
  closeHandler: () => void;
  viewMode?: ModalWrapperViewMode;
}

export interface IActionButtonProps {
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  name: string;
  size?: ButtonProps['size'];
  type?: ButtonNewType;
}

export const ModalWrapper = ({
  title,
  children,
  size,
  width,
  actionsPanel,
  closeHandler,
  className,
  classes,
  viewMode = 'primary',
}: IModalWrapperProps) => {
  const { i18n } = useTranslation();

  return (
    <ModalPortal>
      <div data-view={viewMode} className={cn(styles.formModal, className)}>
        <div className={cn(styles.formModalInner, size && styles[size], width && styles[width], classes?.inner)}>
          <div className={cn(styles.formModalHeader, classes?.header)}>
            <div className={cn(styles.formModalTitle, classes?.title)}>{i18n.t(title as KeyMapKeys)}</div>
            <div className={styles.formModalClose}>
              <XIcon onClick={closeHandler} />
            </div>
          </div>
          <div className={cn(styles.formModalBody, classes?.body)}>{children}</div>
          {actionsPanel && <div className={styles.formModalActionsPanel}>{actionsPanel}</div>}
        </div>
      </div>
    </ModalPortal>
  );
};

export const ModalWrapperActions = ({
  confirmButtonProps,
  closeButtonProps,
  closeHandler,
  viewMode = 'primary',
}: IModalWrapperActions) => {
  return (
    <div data-view={viewMode} className={formStyles.actions}>
      {confirmButtonProps && (
        <ButtonNew
          className={confirmButtonProps.className}
          disabled={confirmButtonProps.disabled}
          isLoading={confirmButtonProps.isLoading}
          onClick={confirmButtonProps.onClick}
          size={confirmButtonProps.size ?? 'middle'}
          type={confirmButtonProps.type ?? 'primary'}>
          {confirmButtonProps.name}
        </ButtonNew>
      )}
      <ButtonNew
        className={closeButtonProps?.className}
        onClick={closeHandler}
        size={closeButtonProps?.size ?? 'middle'}
        type={closeButtonProps?.type ?? 'secondary'}>
        {keyMap.sharedActionsCancel}
      </ButtonNew>
    </div>
  );
};
